import { useMemo } from "react";
import { useGetUserDetail, useProperty } from "@services";
import { verificationDisplayState } from "./VerificationBadge";

/** A property as returned by the ListingManagement list, narrowed to the fields
 * the verification alert / modal need. Verification fields are optional until the
 * backend exposes them on the list DTO (tracked separately) — while absent,
 * every property reads as unverified. */
export interface UnverifiedProperty {
  propertyId: number;
  propertyName?: string;
  propertyLocation?: string;
  propertyImage?: string;
  permitVerificationStatus?: number | null;
  permitExpiryDate?: string | null;
}

/**
 * Shared source of truth for the header alert (MS-484) and the verify modal
 * (MS-485): whether the MT feature is active for this host, and which of their
 * properties still need verification.
 *
 * Feature visibility is driven by the backend flags on GetUserDetail
 * (`requiresMtVerification` gates the whole feature to KSA hosts,
 * `hasUnverifiedProperties` says ≥1 unit is not yet verified). The unverified
 * *list* is derived from the (unpaginated) property list filtered by
 * `permitVerificationStatus`; the fetch is gated on the flags so non-KSA / fully
 * verified accounts never pay for it.
 */
export const useUnverifiedProperties = () => {
  const { data: userDetail } = useGetUserDetail();
  const flags = userDetail?.result;
  const requiresMtVerification = !!flags?.requiresMtVerification;
  const hasUnverifiedProperties = !!flags?.hasUnverifiedProperties;
  const featureOn = requiresMtVerification && hasUnverifiedProperties;

  const { getManagementListQuery } = useProperty();
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = getManagementListQuery(0, 0, { enabled: featureOn });

  const properties: UnverifiedProperty[] = Array.isArray(data) ? data : [];

  const unverified = useMemo(
    () =>
      properties.filter(
        (p) =>
          verificationDisplayState(p.permitVerificationStatus, p.permitExpiryDate) !==
          "verified"
      ),
    [properties]
  );

  return {
    /** MT feature active AND at least one property still needs verification. */
    featureOn,
    requiresMtVerification,
    hasUnverifiedProperties,
    /** Properties still needing verification (all, until the status field lands). */
    unverified,
    count: unverified.length,
    isLoading,
    isFetching,
    refetch,
  };
};

export default useUnverifiedProperties;
